import { useMutation } from "@tanstack/react-query";
import type { Editor } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTRPC } from "../api.ts";
import { contenidoDesdeEditor } from "./nodos.ts";

/** Cuánto se espera desde la última tecla antes de guardar. */
const ESPERA_MS = 2000;
/** Cuánto se espera para reintentar un guardado que falló (sin red, servidor caído). */
const REINTENTO_MS = 10_000;

export type EstadoGuardado =
  | { estado: "guardado"; guardadoEn: Date | null }
  | { estado: "pendiente"; guardadoEn: Date | null }
  | { estado: "guardando"; guardadoEn: Date | null }
  | { estado: "error"; guardadoEn: Date | null; mensaje: string }
  | { estado: "conflicto"; guardadoEn: Date | null; mensaje: string };

/**
 * Autoguardado de la pantalla 05: dos segundos después del último cambio se manda el JSON
 * entero con el hash que se conocía; la API devuelve el nuevo. Al salir de la pantalla con un
 * cambio pendiente se guarda igual, sin esperar. Un conflicto (otra pestaña guardó antes) para
 * el autoguardado hasta que la persona recargue: no se pisa lo ajeno.
 */
export function useAutoguardado(opciones: {
  revisionId: string;
  activo: boolean;
  hashInicial: string | null;
  guardadoInicial: Date | null;
}) {
  const { revisionId, activo, hashInicial, guardadoInicial } = opciones;
  const trpc = useTRPC();
  const guardar = useMutation(trpc.contenido.guardar.mutationOptions());
  const [estado, setEstado] = useState<EstadoGuardado>({
    estado: "guardado",
    guardadoEn: guardadoInicial,
  });
  const { mutateAsync } = guardar;
  const hash = useRef(hashInicial);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const detenido = useRef(false);
  /** Un guardado a la vez: el hash que el segundo manda es el que devolvió el primero. */
  const enVuelo = useRef<Promise<void> | null>(null);
  const repetir = useRef(false);
  /** Cambios hechos y hasta cuál llegó al servidor: si difieren, hay algo sin guardar. */
  const cambios = useRef(0);
  const guardadoHasta = useRef(0);

  // Declarado antes de `guardarAhora` porque el reintento lo usa.
  const ultimoGuardar = useRef<() => Promise<void>>(async () => {});

  const guardarAhora = useCallback(async (): Promise<void> => {
    const editor = editorRef.current;
    if (!editor || detenido.current || !activo) return;
    if (temporizador.current) {
      clearTimeout(temporizador.current);
      temporizador.current = null;
    }
    if (enVuelo.current) {
      // Hay uno en camino: cuando vuelva, se guarda otra vez con lo más reciente. Quien llamó
      // espera también esa repetición (la lanza el primero al terminar su viaje).
      repetir.current = true;
      while (enVuelo.current) await enVuelo.current;
      return;
    }
    const hasta = cambios.current;
    const r = contenidoDesdeEditor(editor.getJSON());
    if (!r.ok) {
      setEstado((e) => ({ estado: "error", guardadoEn: e.guardadoEn, mensaje: r.problema }));
      return;
    }
    setEstado((e) => ({ estado: "guardando", guardadoEn: e.guardadoEn }));
    const viaje = (async () => {
      try {
        const salida = await mutateAsync({
          revisionId,
          contenido: r.contenido,
          ...(hash.current ? { hashConocido: hash.current } : {}),
        });
        hash.current = salida.hashContenido;
        guardadoHasta.current = hasta;
        setEstado({ estado: "guardado", guardadoEn: salida.guardadoEn ?? new Date() });
      } catch (e) {
        const codigo = (e as { data?: { code?: string } }).data?.code;
        // Sin red, el navegador da su error en inglés ("Failed to fetch").
        const sinRed = e instanceof TypeError || (e as Error)?.cause instanceof TypeError;
        const mensaje = sinRed
          ? "no hay conexión con el servidor; se reintenta solo"
          : e instanceof Error
            ? e.message
            : "error desconocido";
        if (codigo === "CONFLICT") {
          detenido.current = true;
          setEstado((s) => ({ estado: "conflicto", guardadoEn: s.guardadoEn, mensaje }));
        } else {
          setEstado((s) => ({ estado: "error", guardadoEn: s.guardadoEn, mensaje }));
          // Un fallo de red no deja lo escrito en el aire: se reintenta solo, y mientras tanto
          // cerrar la pestaña avisa y salir de la pantalla vuelve a intentar.
          if (!temporizador.current) {
            temporizador.current = setTimeout(() => void ultimoGuardar.current(), REINTENTO_MS);
          }
        }
      } finally {
        enVuelo.current = null;
      }
    })();
    enVuelo.current = viaje;
    await viaje;
    if (repetir.current) {
      repetir.current = false;
      await guardarAhora();
    }
  }, [activo, mutateAsync, revisionId]);

  /** Lo llama el editor en cada cambio. */
  const marcarCambio = useCallback(
    (editor: Editor) => {
      editorRef.current = editor;
      if (!activo || detenido.current) return;
      cambios.current++;
      setEstado((e) =>
        e.estado === "pendiente" ? e : { estado: "pendiente", guardadoEn: e.guardadoEn },
      );
      if (temporizador.current) clearTimeout(temporizador.current);
      temporizador.current = setTimeout(() => void guardarAhora(), ESPERA_MS);
    },
    [activo, guardarAhora],
  );

  // Al salir de la pantalla, lo pendiente se guarda sin esperar los dos segundos. Solo al
  // desmontar: con `guardarAhora` como dependencia, cada render disparaba un guardado.
  ultimoGuardar.current = guardarAhora;
  useEffect(() => {
    return () => {
      if (temporizador.current) void ultimoGuardar.current();
    };
  }, []);

  // Cerrar la pestaña con un cambio pendiente pide confirmación (el navegador pone el texto).
  useEffect(() => {
    const avisar = (e: BeforeUnloadEvent) => {
      if (
        temporizador.current ||
        estado.estado === "guardando" ||
        cambios.current !== guardadoHasta.current
      ) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [estado.estado]);

  return {
    estado,
    marcarCambio,
    guardarAhora,
    /** Todo lo escrito llegó al servidor (nada pendiente, en camino ni fallido). */
    estaGuardado: () =>
      !detenido.current && !enVuelo.current && cambios.current === guardadoHasta.current,
  };
}

/** "Guardado hace 12 s", que se recalcula cada 5 s mientras la pantalla está abierta. */
export function useHaceCuanto(fecha: Date | null): string {
  const [, tic] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tic((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);
  if (!fecha) return "";
  const segundos = Math.max(0, Math.round((Date.now() - fecha.getTime()) / 1000));
  if (segundos < 60) return `hace ${segundos} s`;
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `hace ${minutos} min`;
  return `a las ${fecha.toLocaleTimeString("es-HN", { hour: "2-digit", minute: "2-digit" })}`;
}
